/**
 * Analysis recordings and seizures (`app/services/recording_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { volumeStore } from '@brainfocus/web/api/volume_store';
import { EmptyObject } from '@brainfocus/web/utils/types';
import { assertPojo } from '@brainfocus/web/api/response';
import type { SeizureGroup, Recording } from '@brainfocus/web/api/types';

export async function getRecordingInfo(id: number): Promise<ApiRecordingInfo> {
    const r = await client.request('POST', `/api/get-recording-info`, {
        recording_id: id,
    });
    assertPojo<ApiRecordingInfo>(r);
    return r;
}

/**
 * Get all recordings (uploaded EDFs) for a specific patient
 */
export async function getRecordings(patientId: number): Promise<Recording[]> {
    const response = await client.request('POST', '/api/get-recordings', {
        patientId: patientId,
    });

    assertPojo<{ recordings: Recording[] }>(response);
    return response.recordings;
}

/**
 * Delete a recording (uploaded EDF) and all its associated analyses
 */
export async function deleteRecording(recordingId: number): Promise<EmptyObject> {
    const response = await client.request('POST', '/api/delete-recording', {
        recordingId: recordingId,
    });

    assertPojo<EmptyObject>(response);
    // The MRI payloads' coordinate consistency report is computed against the patient's EDFs (#183).
    volumeStore.invalidateAllMri();
    return response;
}

/**
 * Update a recording's title
 */
export async function updateRecording(recordingId: number, title: string): Promise<EmptyObject> {
    const response = await client.request('POST', '/api/update-recording', {
        recordingId: recordingId,
        title: title,
    });

    assertPojo<EmptyObject>(response);
    return response;
}

/**
 * Get detail of patient belonging to user's insitution
 */
export async function getPatientsSeizures(patientId: number): Promise<SeizureGroup[]> {
    const response = await client.request('POST', '/api/get-seizures', { patientId });

    assertPojo<SeizureGroup[]>(response);
    return response;
}

export interface ApiRecordingInfo {
    diverged: boolean;
    /**
     * Reason the recording diverged, when known (#733). `'norm_type'` indicates
     * the normalization mode changed; `null` for the generic input-change case.
     */
    divergedReason?: string | null;
    /**
     * ISO timestamp of the last re-referencing update (#764), `null` when the
     * stored signal is still the one the analysis was first computed with. A
     * change means the preprocessed traces and the TC matrices were rewritten.
     */
    rereferencedAt?: string | null;
    status: 'RecordingStatus.PROCESSING' | 'RecordingStatus.DONE' | 'RecordingStatus.FAILED';
    mri: 'processing' | 'available' | 'none';
}
